/**
 * HTML-to-Canvas Parser
 * Renders the certificate HTML in a hidden iframe to get real rendered positions
 * and computed styles, then converts each visual element into a draggable canvas element.
 */
import {
  CanvasElement,
  CanvasElementType,
  TextCanvasElement,
  VariableCanvasElement,
  ImageCanvasElement,
  LineCanvasElement,
  CanvasConfig,
  CanvasBackground,
  BorderConfig,
  DecorativeBorderConfig,
  TextStyle,
  DEFAULT_TEXT_STYLE,
  DEFAULT_CANVAS_CONFIG,
  generateElementId
} from '@/types/certificate-template.types';

interface ParsedTemplate {
  elements: CanvasElement[];
  canvasConfig: CanvasConfig;
}

const INLINE_TAGS = new Set([
  'span', 'strong', 'b', 'em', 'i', 'u', 'a', 'small', 'sub', 'sup', 'br', 'mark', 'code', 'abbr'
]);

const DECORATIVE_CLASSES = new Set([
  'outer-border', 'inner-border', 'corner', 'corner-tl', 'corner-tr', 'corner-bl', 'corner-br'
]);

function colorToHex(color: string): string {
  if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return 'transparent';
  if (color.startsWith('#')) return color;
  if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      return '#' + match.slice(0, 3).map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
    }
  }
  return color;
}

function parseFontWeight(weight: string): TextStyle['fontWeight'] {
  if (weight === 'bold' || weight === '700') return 'bold';
  if (weight === 'normal' || weight === '400') return 'normal';
  const numericWeights = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];
  if (numericWeights.includes(weight)) return weight as TextStyle['fontWeight'];
  return 'normal';
}

function parseTextAlign(align: string): TextStyle['textAlign'] {
  if (['left', 'center', 'right', 'justify'].includes(align)) return align as TextStyle['textAlign'];
  return 'center';
}

// Fallback hidden check for browsers without checkVisibility(); same rule as the text check.
function isHiddenByStyle(style: CSSStyleDeclaration): boolean {
  return style.display === 'none' || style.visibility === 'hidden';
}

function isDecorative(el: HTMLElement): boolean {
  for (const cls of el.classList) {
    if (DECORATIVE_CLASSES.has(cls)) return true;
  }
  return false;
}

/** The {{skillzen_logo_url}} placeholder is a broken image here, but the PDF prints the real logo (8334x2917).
 *  Give it the same size so the editor measures everything below the logo where the PDF puts it. */
export const SKILLZEN_LOGO_SIZE_CSS =
  `img[src="{{skillzen_logo_url}}"]{content:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8334' height='2917'/%3E");}`;

/**
 * Parse certificate HTML template into canvas elements.
 * Creates a hidden iframe, renders the full HTML (with CSS classes),
 * then extracts elements with their real rendered positions and styles.
 */
export function parseHtmlToCanvasElements(html: string): Promise<ParsedTemplate> {
  return new Promise((resolve) => {
    try {
      // 1. Strip Handlebars conditionals — keep "if" block, discard "else"
      let processedHtml = html.replace(
        /\{\{#if\s+\w+\}\}([\s\S]*?)(?:\{\{else\}\}[\s\S]*?)?\{\{\/if\}\}/g,
        '$1'
      );

      // 2. Strip previous canvas editor artifacts
      processedHtml = processedHtml
        .replace(/<!--CANVAS_LAYOUT:[\s\S]*?:END_CANVAS_LAYOUT-->/g, '')
        .replace(/<!--CANVAS_VARS:[\s\S]*?:END_CANVAS_VARS-->/g, '')
        .replace(/<div\s+data-canvas-var[^>]*>[\s\S]*?<\/div>/gi, '');

      // 3. Override body layout to match background iframe exactly (no flex centering)
      processedHtml = processedHtml.replace('</head>',
        `<style>body{margin:0!important;padding:0!important;display:block!important;min-height:auto!important;overflow:hidden!important;}${SKILLZEN_LOGO_SIZE_CSS}</style></head>`);



      // 4. Create hidden sandboxed iframe — same size as canvas (842x595) for exact position match
      // sandbox="allow-same-origin" lets us read computed styles but blocks script execution
      const iframe = document.createElement('iframe');
      iframe.setAttribute('sandbox', 'allow-same-origin');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:842px;height:595px;border:none;opacity:0;pointer-events:none;';

      let resolved = false;
      const safeResolve = (result: ParsedTemplate) => {
        if (resolved) return;
        resolved = true;
        if (iframe.parentElement) document.body.removeChild(iframe);
        resolve(result);
      };

      iframe.onload = () => {
        try {
          const result = extractFromIframe(iframe);
          safeResolve(result);
        } catch (err) {
          // Extraction error — return empty
          safeResolve({ elements: [], canvasConfig: { ...DEFAULT_CANVAS_CONFIG } });
        }
      };

      iframe.onerror = () => {
        // Iframe load error — return empty
        safeResolve({ elements: [], canvasConfig: { ...DEFAULT_CANVAS_CONFIG } });
      };

      // Fallback timeout in case onload doesn't fire
      setTimeout(() => {
        if (!resolved) {
          // Fallback timeout — try extracting now
          try {
            const result = extractFromIframe(iframe);
            safeResolve(result);
          } catch {
            safeResolve({ elements: [], canvasConfig: { ...DEFAULT_CANVAS_CONFIG } });
          }
        }
      }, 1000);

      iframe.srcdoc = processedHtml;
      document.body.appendChild(iframe);
    } catch (err) {
      // Setup error — return empty
      resolve({ elements: [], canvasConfig: { ...DEFAULT_CANVAS_CONFIG } });
    }
  });
}

/** Extract all visual elements from a rendered iframe */
function extractFromIframe(iframe: HTMLIFrameElement): ParsedTemplate {
  const win = iframe.contentWindow!;
  const doc = iframe.contentDocument!;

  if (!win || !doc) {
    throw new Error('Cannot access iframe content');
  }

  const elements: CanvasElement[] = [];
  let zIndex = 1;

  // Find the certificate container
  const certContainer = doc.querySelector('.certificate') as HTMLElement || doc.body;
  const certRect = certContainer.getBoundingClientRect();
  const canvasWidth = Math.round(certRect.width) || DEFAULT_CANVAS_CONFIG.width;
  const canvasHeight = Math.round(certRect.height) || DEFAULT_CANVAS_CONFIG.height;



  // Extract background
  const certComputed = win.getComputedStyle(certContainer);
  const rawBg = colorToHex(certComputed.backgroundColor);
  const background: CanvasBackground = {
    type: 'solid',
    color: rawBg === 'transparent' ? '#FFFFFF' : rawBg
  };

  // Extract decorative borders (outer, inner, corners)
  let outerBorder: BorderConfig | undefined;
  let decorativeBorder: DecorativeBorderConfig | undefined;

  const outerBorderEl = doc.querySelector('.outer-border') as HTMLElement | null;
  const innerBorderEl = doc.querySelector('.inner-border') as HTMLElement | null;
  const cornerEl = doc.querySelector('.corner') as HTMLElement | null;



  // Also search broadly: any absolutely-positioned empty element with a border
  if (!outerBorderEl && !innerBorderEl && !cornerEl) {
    const allEls = certContainer.querySelectorAll('*');
    const borderCandidates: HTMLElement[] = [];
    allEls.forEach(el => {
      const cs = win.getComputedStyle(el as HTMLElement);
      const text = (el as HTMLElement).textContent?.trim() || '';
      const bw = parseInt(cs.borderTopWidth) || parseInt(cs.borderLeftWidth) || 0;
      if (cs.position === 'absolute' && !text && bw > 0 && el.children.length === 0) {
        borderCandidates.push(el as HTMLElement);
      }
    });
    // Assign candidates: first = outer, second = inner, small ones = corners
    const bigBorders = borderCandidates.filter(el => {
      const r = el.getBoundingClientRect();
      return r.width > 100 && r.height > 100;
    });
    const smallBorders = borderCandidates.filter(el => {
      const r = el.getBoundingClientRect();
      return r.width <= 30 && r.height <= 30;
    });
    if (bigBorders.length > 0 || smallBorders.length > 0) {
      decorativeBorder = {};
      if (bigBorders[0]) {
        const bc = win.getComputedStyle(bigBorders[0]);
        decorativeBorder.outer = {
          enabled: true,
          width: parseInt(bc.borderTopWidth) || 1,
          style: (bc.borderTopStyle || 'solid') as any,
          color: colorToHex(bc.borderTopColor),
          inset: {
            top: parseInt(bc.top) || 25, left: parseInt(bc.left) || 35,
            right: parseInt(bc.right) || 35, bottom: parseInt(bc.bottom) || 25
          }
        };
        outerBorder = { width: decorativeBorder.outer.width, style: decorativeBorder.outer.style, color: decorativeBorder.outer.color };
      }
      if (bigBorders[1]) {
        const bc = win.getComputedStyle(bigBorders[1]);
        decorativeBorder.inner = {
          enabled: true,
          width: parseInt(bc.borderTopWidth) || 2,
          style: (bc.borderTopStyle || 'solid') as any,
          color: colorToHex(bc.borderTopColor),
          inset: {
            top: parseInt(bc.top) || 35, left: parseInt(bc.left) || 45,
            right: parseInt(bc.right) || 45, bottom: parseInt(bc.bottom) || 35
          }
        };
      }
      if (smallBorders.length >= 4) {
        const bc = win.getComputedStyle(smallBorders[0]);
        decorativeBorder.corners = {
          enabled: true,
          color: colorToHex(bc.borderTopColor || bc.borderLeftColor),
          size: parseInt(bc.width) || 15,
          width: parseInt(bc.borderTopWidth) || parseInt(bc.borderLeftWidth) || 2,
          inset: {
            top: parseInt(win.getComputedStyle(smallBorders[0]).top) || 43,
            left: parseInt(win.getComputedStyle(smallBorders[0]).left) || 53,
            right: parseInt(win.getComputedStyle(smallBorders[1]).right) || 53,
            bottom: parseInt(win.getComputedStyle(smallBorders[2]).bottom) || 43
          }
        };
      }
    }
  }

  if (outerBorderEl || innerBorderEl || cornerEl) {
    decorativeBorder = {};

    if (outerBorderEl) {
      const bc = win.getComputedStyle(outerBorderEl);
      const bw = parseInt(bc.borderTopWidth) || parseInt(bc.borderLeftWidth) || 1;
      const bStyle = (bc.borderTopStyle || bc.borderLeftStyle || 'solid') as BorderConfig['style'];
      const bColor = colorToHex(bc.borderTopColor || bc.borderLeftColor || '#3B82F6');
      decorativeBorder.outer = {
        enabled: true,
        width: bw,
        style: bStyle,
        color: bColor,
        inset: {
          top: parseInt(bc.top) || 25,
          left: parseInt(bc.left) || 35,
          right: parseInt(bc.right) || 35,
          bottom: parseInt(bc.bottom) || 25
        }
      };
      outerBorder = { width: bw, style: bStyle, color: bColor };
    }

    if (innerBorderEl) {
      const bc = win.getComputedStyle(innerBorderEl);
      const bw = parseInt(bc.borderTopWidth) || 0;
      if (bw > 0) {
        decorativeBorder.inner = {
          enabled: true,
          width: bw,
          style: (bc.borderTopStyle || 'solid') as BorderConfig['style'],
          color: colorToHex(bc.borderTopColor),
          inset: {
            top: parseInt(bc.top) || 35,
            left: parseInt(bc.left) || 45,
            right: parseInt(bc.right) || 45,
            bottom: parseInt(bc.bottom) || 35
          }
        };
        if (!outerBorder) {
          outerBorder = { width: bw, style: decorativeBorder.inner.style, color: decorativeBorder.inner.color };
        }
      }
    }

    if (cornerEl) {
      const bc = win.getComputedStyle(cornerEl);
      const cornerColor = colorToHex(bc.borderTopColor || bc.borderLeftColor || '#1E40AF');
      const cornerW = parseInt(bc.width) || 15;
      const cornerBw = parseInt(bc.borderTopWidth) || parseInt(bc.borderLeftWidth) || 2;
      // Get corner inset from .corner-tl position
      const tlEl = doc.querySelector('.corner-tl') as HTMLElement | null;
      const tlComp = tlEl ? win.getComputedStyle(tlEl) : null;
      decorativeBorder.corners = {
        enabled: true,
        color: cornerColor,
        size: cornerW,
        width: cornerBw,
        inset: {
          top: parseInt(tlComp?.top || '') || 43,
          left: parseInt(tlComp?.left || '') || 53,
          right: parseInt(win.getComputedStyle(doc.querySelector('.corner-tr') as HTMLElement || cornerEl).right) || 53,
          bottom: parseInt(win.getComputedStyle(doc.querySelector('.corner-bl') as HTMLElement || cornerEl).bottom) || 43
        }
      };
    }
  }

  // Ensure all 3 border parts exist — fill missing with defaults
  if (decorativeBorder) {
    if (!decorativeBorder.outer) {
      decorativeBorder.outer = {
        enabled: true, width: 1, style: 'solid',
        color: decorativeBorder.inner?.color || '#3B82F6',
        inset: { top: 25, left: 35, right: 35, bottom: 25 }
      };
    }
    if (!decorativeBorder.inner) {
      decorativeBorder.inner = {
        enabled: true, width: 2, style: 'solid',
        color: decorativeBorder.outer?.color || '#1E40AF',
        inset: { top: 35, left: 45, right: 45, bottom: 35 }
      };
    }
    if (!decorativeBorder.corners) {
      decorativeBorder.corners = {
        enabled: true, size: 15, width: 2,
        color: decorativeBorder.inner?.color || '#1E40AF',
        inset: { top: 43, left: 53, right: 53, bottom: 43 }
      };
    }
  }

  const extracted = new Set<Node>();

  // --- Extract images ---
  certContainer.querySelectorAll('img').forEach((imgEl) => {
    const img = imgEl as HTMLImageElement;
    const src = img.getAttribute('src') || '';
    // Skip images hidden by CSS (display:none / visibility:hidden on it or a parent),
    // same as text below — otherwise they became visible ghost images after save.
    const vis = (img as HTMLImageElement & { checkVisibility?: (o?: object) => boolean }).checkVisibility;
    // both option names: checkVisibilityCSS (older Chrome) and visibilityProperty (newer)
    if (vis ? !vis.call(img, { checkVisibilityCSS: true, visibilityProperty: true }) : isHiddenByStyle(win.getComputedStyle(img))) return;
    const rect = img.getBoundingClientRect();

    let x = Math.round(rect.left - certRect.left);
    let y = Math.round(rect.top - certRect.top);
    let w = Math.round(rect.width);
    let h = Math.round(rect.height);

    // Broken images (variable src like {{logo_url}}) may render tiny.
    // Try multiple fallbacks: HTML attributes, parent container size, computed styles.
    if (w < 10 || h < 10) {
      const attrW = parseInt(img.getAttribute('width') || '');
      const attrH = parseInt(img.getAttribute('height') || '');
      if (attrW > 10 && attrH > 10) {
        w = attrW;
        h = attrH;
      } else if (img.parentElement) {
        const parentRect = img.parentElement.getBoundingClientRect();
        const pw = Math.round(parentRect.width);
        const ph = Math.round(parentRect.height);
        if (pw > 10 && ph > 10) {
          w = pw;
          h = ph;
        } else {
          const imgComp = win.getComputedStyle(img);
          w = parseInt(imgComp.maxWidth) || parseInt(imgComp.width) || 150;
          h = parseInt(imgComp.maxHeight) || parseInt(imgComp.height) || 45;
        }
      } else {
        const imgComp = win.getComputedStyle(img);
        w = parseInt(imgComp.maxWidth) || parseInt(imgComp.width) || 150;
        h = parseInt(imgComp.maxHeight) || parseInt(imgComp.height) || 45;
      }
    }

    // Skip truly invisible images
    if (w < 3 && h < 3) return;

    extracted.add(img);
    if (img.parentElement && !(img.parentElement.textContent?.trim())) {
      extracted.add(img.parentElement);
    }

    if (src.includes('{{')) {
      const varMatch = src.match(/\{\{(\w+)\}\}/);
      if (varMatch) {
        elements.push({
          id: generateElementId(),
          type: CanvasElementType.VARIABLE,
          name: varMatch[1].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          variableKey: varMatch[1],
          placeholder: `{{${varMatch[1]}}}`,
          style: { ...DEFAULT_TEXT_STYLE, fontSize: 12 },
          format: 'text',
          position: { x, y, width: w, height: h, zIndex: zIndex++ },
          locked: false,
          visible: true,
          opacity: 1
        } as VariableCanvasElement);
      }
    } else if (src) {
      elements.push({
        id: generateElementId(),
        type: CanvasElementType.IMAGE,
        name: img.alt || 'Image',
        src,
        alt: img.alt || '',
        fit: 'contain',
        position: { x, y, width: w, height: h, zIndex: zIndex++ },
        locked: false,
        visible: true,
        opacity: 1
      } as ImageCanvasElement);
    }
  });

  // --- Extract decorative lines (colored divs with small height, no text) ---
  certContainer.querySelectorAll('div').forEach(div => {
    if (extracted.has(div)) return;
    if (isDecorative(div)) return;
    const el = div as HTMLElement;
    const text = el.textContent?.trim() || '';
    if (text) return; // has text — not a line
    if (el.children.length > 0) return; // has children — not a simple line
    const cs = win.getComputedStyle(el);
    const bgColor = cs.backgroundColor;
    if (!bgColor || bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') return;
    const rect = el.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    // Detect as line: colored, no text, height <= 8px (thin) or width <= 8px (vertical)
    if (h > 8 && w > 8) return; // too large — not a line element
    if (w < 3 && h < 3) return; // too tiny
    extracted.add(el);
    const x = Math.round(rect.left - certRect.left);
    const y = Math.round(rect.top - certRect.top);
    const isVertical = h > w;
    elements.push({
      id: generateElementId(),
      type: CanvasElementType.LINE,
      name: 'Decorative Line',
      strokeWidth: isVertical ? Math.max(w, 1) : Math.max(h, 1),
      strokeColor: colorToHex(bgColor) || '#000000',
      strokeStyle: 'solid',
      startPoint: { x: 0, y: 0 },
      endPoint: isVertical ? { x: 0, y: h } : { x: w, y: 0 },
      position: { x, y, width: Math.max(w, isVertical ? 2 : 10), height: Math.max(h, isVertical ? 10 : 2), zIndex: zIndex++ },
      locked: false,
      visible: true,
      opacity: 1
    } as LineCanvasElement);
  });

  // --- Extract text elements by walking the DOM tree ---
  function walkForText(root: HTMLElement) {
    const tag = root.tagName.toLowerCase();
    if (['style', 'script', 'link', 'meta', 'head', 'img'].includes(tag)) return;
    if (extracted.has(root)) return;
    if (isDecorative(root)) return;

    const computed = win.getComputedStyle(root);
    if (computed.display === 'none' || computed.visibility === 'hidden') return;

    const rect = root.getBoundingClientRect();
    if (rect.width < 3 || rect.height < 3) return;

    const text = root.textContent?.trim() || '';
    if (!text) return;

    // Get block-level children (not inline wrappers)
    const blockChildren = Array.from(root.children).filter(c => {
      if (extracted.has(c)) return false;
      const cTag = c.tagName.toLowerCase();
      if (INLINE_TAGS.has(cTag) || cTag === 'img') return false;
      const cComp = win.getComputedStyle(c as HTMLElement);
      return cComp.display !== 'inline' && cComp.display !== 'inline-block';
    }) as HTMLElement[];

    if (blockChildren.length > 0) {
      // Container — recurse into block children
      blockChildren.forEach(child => walkForText(child));
      return;
    }

    // Leaf element — extract as canvas element
    extracted.add(root);

    // Use the element's own bounding rect for all position values.
    // This matches the background iframe exactly since both render the same HTML.
    // Using Range rect for tight text bounds breaks centering — when the element's
    // width is shrunk to the text width, text-align:center has no room to work,
    // causing visually centered text to appear left-aligned in the editor.
    const x = Math.round(rect.left - certRect.left);
    const y = Math.round(rect.top - certRect.top);
    const w = Math.max(Math.round(rect.width), 50);
    const h = Math.max(Math.round(rect.height), 20);

    // Skip out-of-bounds
    if (x + w < 0 || y + h < 0 || x > canvasWidth || y > canvasHeight) return;

    const textStyle: TextStyle = {
      fontFamily: computed.fontFamily || DEFAULT_TEXT_STYLE.fontFamily,
      fontSize: parseInt(computed.fontSize) || DEFAULT_TEXT_STYLE.fontSize,
      fontWeight: parseFontWeight(computed.fontWeight),
      fontStyle: computed.fontStyle === 'italic' ? 'italic' : 'normal',
      textDecoration: (computed.textDecorationLine || computed.textDecoration || '').includes('underline')
        ? 'underline' : 'none',
      textAlign: parseTextAlign(computed.textAlign),
      lineHeight: computed.lineHeight === 'normal'
        ? 1.4
        : parseFloat(computed.lineHeight) / (parseInt(computed.fontSize) || 16),
      letterSpacing: computed.letterSpacing === 'normal' ? 0 : (parseFloat(computed.letterSpacing) || 0),
      color: colorToHex(computed.color),
      backgroundColor: (() => {
        const bg = colorToHex(computed.backgroundColor);
        return (bg && bg !== 'transparent' && bg !== '#ffffff') ? bg : undefined;
      })(),
      padding: parseInt(computed.paddingTop) > 2 ? parseInt(computed.paddingTop) : undefined,
      borderRadius: parseInt(computed.borderRadius) > 0 ? parseInt(computed.borderRadius) : undefined
    };

    // Exact variable: {{var_name}}
    const exactVar = text.match(/^\{\{(\w+)\}\}$/);
    if (exactVar) {
      elements.push({
        id: generateElementId(),
        type: CanvasElementType.VARIABLE,
        name: exactVar[1].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        variableKey: exactVar[1],
        placeholder: text,
        style: textStyle,
        format: exactVar[1].includes('date') ? 'date' : 'text',
        position: { x, y, width: w, height: h, zIndex: zIndex++ },
        locked: false,
        visible: true,
        opacity: 1
      } as VariableCanvasElement);
      return;
    }

    // Mixed text with variables: "{{total_courses}} courses completed"
    if (text.includes('{{') && text.includes('}}')) {
      const varMatch = text.match(/\{\{(\w+)\}\}/);
      if (varMatch) {
        elements.push({
          id: generateElementId(),
          type: CanvasElementType.VARIABLE,
          name: varMatch[1].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          variableKey: varMatch[1],
          placeholder: text,
          style: textStyle,
          format: 'text',
          position: { x, y, width: w, height: h, zIndex: zIndex++ },
          locked: false,
          visible: true,
          opacity: 1
        } as VariableCanvasElement);
        return;
      }
    }

    // Regular text
    elements.push({
      id: generateElementId(),
      type: CanvasElementType.TEXT,
      name: text.length > 25 ? text.substring(0, 25) + '...' : text,
      content: text,
      style: textStyle,
      position: { x, y, width: w, height: h, zIndex: zIndex++ },
      locked: false,
      visible: true,
      opacity: 1
    } as TextCanvasElement);
  }

  walkForText(certContainer as HTMLElement);

  // Clamp elements within the innermost border area (or canvas bounds if no border).
  // Prevents QR codes and edge elements from overlapping decorative borders.
  const outerInset = decorativeBorder?.outer?.enabled ? decorativeBorder.outer.inset : null;
  const clampLeft = outerInset ? outerInset.left + 5 : 0;
  const clampTop = outerInset ? outerInset.top + 5 : 0;
  const clampRight = outerInset ? canvasWidth - outerInset.right - 5 : canvasWidth;
  const clampBottom = outerInset ? canvasHeight - outerInset.bottom - 5 : canvasHeight;

  for (const el of elements) {
    const pos = el.position;
    if (pos.x + pos.width > clampRight) {
      pos.x = clampRight - pos.width;
    }
    if (pos.y + pos.height > clampBottom) {
      pos.y = clampBottom - pos.height;
    }
    if (pos.x < clampLeft) pos.x = clampLeft;
    if (pos.y < clampTop) pos.y = clampTop;
  }

  return {
    elements,
    canvasConfig: {
      width: canvasWidth,
      height: canvasHeight,
      orientation: canvasWidth > canvasHeight ? 'landscape' : 'portrait',
      background,
      border: outerBorder || DEFAULT_CANVAS_CONFIG.border,
      decorativeBorder,
      padding: DEFAULT_CANVAS_CONFIG.padding
    }
  };
}
